import React, { useMemo } from 'react'
import { InboxOutlined } from '@ant-design/icons'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

type Props = {
  uploadedFiles: File[]
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>
  multiple?: boolean
  acceptedFileTypes?: string
}

type FileWithPreview = File & { localUrl?: string }

function makePreview(file: File): FileWithPreview {
  const f = file as FileWithPreview
  if (!f.localUrl) f.localUrl = URL.createObjectURL(file)
  return f
}

export default function CustomFileUpload({
  uploadedFiles,
  setUploadedFiles,
  multiple = true,
  acceptedFileTypes = '.jpg,.jpeg,.png,.pdf',
}: Props) {
  const { t } = useTranslation(['UploadPOD'])
  const accept = acceptedFileTypes
  const accepted = useMemo(() => accept.split(',').map((s) => s.trim().toLowerCase()), [accept])

  const files = useMemo(() => uploadedFiles.map(makePreview), [uploadedFiles])

  const addFiles = (list: FileList | null) => {
    if (!list) return
    const incoming = Array.from(list).filter((f) => {
      const ext = `.${f.name.split('.').pop()?.toLowerCase() || ''}`
      return accepted.includes(ext) || accepted.includes(f.type)
    })

    setUploadedFiles((prev) => {
      const prevNames = new Set(prev.map((p) => p.name))
      const unique = incoming.filter((nf) => !prevNames.has(nf.name))
      return multiple ? [...prev, ...unique] : unique.slice(0, 1)
    })
  }

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    e.target.value = ''
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    addFiles(e.dataTransfer.files)
  }

  const remove = (name: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.name !== name))
  }

  return (
    <div className="space-y-4">
      {files.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {files.map((file) => (
            <div key={file.name} className="rounded-lg border border-border overflow-hidden bg-background">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
                <div className="truncate text-sm text-foreground" title={file.name}>
                  {file.name}
                </div>
                <Button variant="outline" size="sm" type="button" onClick={() => remove(file.name)}>
                  {t('UploadPOD:upload.remove', 'Remove')}
                </Button>
              </div>
              <div className="p-2">
                {file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') ? (
                  <embed src={file.localUrl} type="application/pdf" className="w-full h-48 rounded" />
                ) : (
                  <img src={file.localUrl} alt={file.name} className="w-full h-48 object-cover rounded" />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div
        className="rounded-lg border border-dashed border-border bg-card p-6 text-center cursor-pointer hover:bg-accent/20 transition-colors"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => document.getElementById('pod-file-input')?.click()}
      >
        <div className="flex flex-col items-center gap-2">
          <InboxOutlined className="text-3xl text-primary" />
          <div className="text-sm font-medium text-foreground">
            {t('UploadPOD:upload.clickDrag', 'Click or drag files to upload')}
          </div>
          <div className="text-xs text-muted-foreground">
            {t('UploadPOD:upload.acceptedFormats', 'Accepted: {{types}}. Max merged size: 25MB.', { types: acceptedFileTypes })}
          </div>
        </div>
        <input
          id="pod-file-input"
          type="file"
          accept={acceptedFileTypes}
          multiple={multiple}
          className="hidden"
          onChange={onPick}
        />
      </div>
    </div>
  )
}

